import { Breakpoints, Disclaimer } from '@vw-marketing/us-components';
import { History } from 'history';
import isBrowser from 'is-browser';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { useFeatureAppConfig } from '../hooks/use-feature-app-config';
import { ImageParams } from '../typing/general';
import { FeatureAppDisclaimers } from '../typing/main';
import { UsedVehicle } from '../typing/used-inventory';
import { BASE_IMAGE_URL } from './app-constants';

export type viewportType = 'desktop' | 'tablet' | 'mobile';
/**
 * Convert the number to thousand format
 * @param value String with a number
 */
export const separateThousand = (value: string) => {
  const intValue = parseInt(value, 10);

  return intValue < 1000
    ? intValue
    : intValue?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**

 * Capitalize an string
 * @param text An string
 */
export const toCapitalize = (text: string): string =>
  text?.charAt(0)?.toUpperCase() + text?.slice(1);

/**
 * Return white or black acording the hexcolor
 * @param hexcolor An hexadecimal color like #000, 000, 000000
 */
export const getHexContrast = (hexcolor: string) => {
  let excolorAux = hexcolor;

  // If a leading # is provided, remove it
  if (excolorAux.slice(0, 1) === '#') {
    excolorAux = excolorAux.slice(1);
  }

  // If a three-character hexcode, make six-character
  if (excolorAux.length === 3) {
    excolorAux = excolorAux
      .split('')
      .map((hex) => hex + hex)
      .join('');
  }

  // Convert to RGB value
  const r = parseInt(excolorAux.substr(0, 2), 16);
  const g = parseInt(excolorAux.substr(2, 2), 16);
  const b = parseInt(excolorAux.substr(4, 2), 16);

  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 128 ? 'black' : 'white';
};

/**
 * Get online buying deeplink
 * @param UsedVehicle
 * @returns Online buying link
 */
export const isOnlineBuyingLinkModified = (vehicle: UsedVehicle) => {
  return !!vehicle.dealerDeeplink;
};

/**
 * Return white or black acording the RGB color
 * @param rgb RGB color with this format [225, 0, 0]
 * the fourth parameter can be passed but will be ignored
 */
export const getRgbContrast = (rgb: any) => {
  // randomly update
  rgb[0] = Math.round(Math.random() * 255);
  rgb[1] = Math.round(Math.random() * 255);
  rgb[2] = Math.round(Math.random() * 255);

  const o = Math.round((rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000);

  return o > 125 ? 'black' : 'white';
};

/**
 * Validate if the color name is a valid css color
 * @param stringToTest Color name or hex color
 */
export const isValidTextColor = (stringToTest: string) => {
  if (!isBrowser) {
    return false;
  }

  // Alter the following conditions according to your need.
  if (stringToTest === '') {
    return false;
  }
  if (stringToTest === 'inherit') {
    return false;
  }
  if (stringToTest === 'transparent') {
    return false;
  }

  const image = document.createElement('img');
  image.style.color = 'rgb(0, 0, 0)';
  image.style.color = stringToTest;
  if (image.style.color !== 'rgb(0, 0, 0)') {
    return true;
  }
  image.style.color = 'rgb(255, 255, 255)';
  image.style.color = stringToTest;

  return image.style.color !== 'rgb(255, 255, 255)';
};

/**
 * Given a color name this function return the hexadecimal value
 * @param str Color name
 */
export const getHexColorByName = (str: string): string => {
  if (!isBrowser) {
    return '#000';
  }

  const ctx = document.createElement('canvas').getContext('2d');
  if (ctx) {
    ctx.fillStyle = str;

    return ctx.fillStyle;
  }

  return '#000';
};

/**
 * Validate if is a valid hex color
 * @param hexColor Hex color format, you can use it without #
 */
export const isValidHexColor = (hexColor: string) => {
  const hexColorRegExp = /(^#?[a-fA-F0-9]{6}$|^#?[a-fA-F0-9]{3}$)/;

  return hexColorRegExp.test(hexColor);
};

/**
 * Return the contrast white or black using the given color
 * @param ele RBG, Color name, or hexadecimal color
 */
export const getContrastColorText = (ele: any) => {
  if (typeof ele === 'string' || ele instanceof String) {
    if (isValidHexColor(ele as string)) {
      return getHexContrast(ele as string);
    }

    if (isValidTextColor(ele as string)) {
      return getHexContrast(getHexColorByName(ele as string) as string);
    }

    return 'black';
  }

  return getRgbContrast(ele);
};

/**
 * Validate if is a valid color or return the default background
 * @param ele Color name, or hexadecimal color
 */
export const getBgColor = (ele: any) => {
  if (typeof ele === 'string' || ele instanceof String) {
    if (isValidHexColor(ele as string) || isValidTextColor(ele as string)) {
      return ele;
    }
  }

  return '#dfe4e8';
};

/**
 * Inject style to the fiiter links to fix them being on top of sticky nav
 */
export const appendFooterFAStyle = () => {
  const style = document.createElement('style');
  style.textContent = `
    @media (max-width: 560px) {
      .footer-links {
        position: inherit;
      }
    }`;
  document.head.appendChild(style);
};

/**
 * Unsets z-index value for pagemain container
 */
export const unsetPagemainZindex = () => {
  // The class name is used due to lack of id
  let pagemainContainer = document.getElementsByClassName('pagemain')[0];
  if (pagemainContainer && pagemainContainer.parentElement)
    pagemainContainer.parentElement.style.zIndex = 'unset';
};

/*
 * Returns the text from a HTML string
 *
 * @param {html} String The html string
 */
export const clearText = (text?: string) =>
  text &&
  text
    .replace(/&quot;/g, '"')
    .replace(/&mdash;/g, '—')
    .replace(/&reg;/g, '®')
    .replace(/&apos;/g, "'")
    .replace(/&acute;/g, '´')
    .replace(/&trade;/g, '™')
    .replace(/&copy;/g, '©');

/**
 * Get viewport defined by screen width
 * @param width
 * @returns
 */
export const getViewportType = (width: number): viewportType => {
  if (width === 0) {
    return 'desktop';
  }
  const isDesktopViewport = window.matchMedia(
    `(min-width: ${Breakpoints.b1280}px)`,
  ).matches;
  if (isDesktopViewport) {
    return 'desktop';
  }

  const isTabletViewport = window.matchMedia(
    `(min-width: ${Breakpoints.b960}px)`,
  ).matches;
  if (isTabletViewport) {
    return 'tablet';
  }
  return 'mobile';
};

/**
 * converts text to lower case validating undefined options
 * @param text
 * @returns string
 */
export const sanitizeToLowerCase = (text: string) => {
  if (!text) return '';
  return text.toLowerCase();
};

/**
 * Give format to the Phone number
 * @param phone Phone number
 */
export const stringToPhoneFormat = (phone: string | undefined) => {
  if(!phone) return
  return phone?.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

}

/**
 * Returns a 2 digits version for 1 digit numbers
 * @param number
 */
export const numberToTwoDigitsString = (number: number) =>
  number < 10 ? ('0' + number)?.slice(-2) : number?.toString();

/**
 * Blocks the scroll action when some layer is active
 * @param toggle
 */
export const blockScrollAction = (toggle: boolean) => {
  if (isBrowser) {
    if (toggle) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }
};

// Scrolls to the top of the document
export const scrollToDocumentTop = () => {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
};

export const getDisclaimerKeys = (
  disclaimers?: FeatureAppDisclaimers,
  useDisclaimerModule?: (
    disclaimerText: string | undefined,
    scope?: string | undefined
  ) => string | undefined
): any[] => {
  const newSavingsDisclaimers: Disclaimer[] = [];
  //results
  disclaimers?.results?.map((disclaimer: Disclaimer) => {
    const disclaimerKey = useDisclaimerModule && useDisclaimerModule(disclaimer.description);
    newSavingsDisclaimers.push({
      ...disclaimer,
      number: disclaimerKey
    });
  });
  return newSavingsDisclaimers;
};

/**
 * Add a query parameter to the url
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const addUrlParam = (history: History, key: string, value: string) => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = { ...query, [key]: value };
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.replace(path);
};

/**
 * Get the correct format for the searh from router history
 * @param history Router history
 */
export const getHistorySearch = (history: History) => {
  let search = history.location.search;

  const query = queryString.parse(decodeURI(search));
  delete query['---'];
  search = isEmpty(query) ? '' : `?${queryString.stringify(query)}`;

  return search.replace(/-app/gi, '');
};

/**
 * Get the correct format for the gallery image urls
 * @param vehicle UsedVehicle model
 */
export const getGalleryImagesUrl = (vehicle: UsedVehicle) => {
  const { photoCount, dealer, stockNum } = vehicle;

  if (!(photoCount && dealer?.dealerid && stockNum)) return [];

  const galleryImages: string[] = [];

  const imageURL = BASE_IMAGE_URL.replace(
    '{DEALERID}',
    dealer.dealerid,
  ).replace('{STOCKNUM}', stockNum);

  for (let index = 1; index <= photoCount; index++) {
    const finalImageURL = imageURL.replace('{SEQNUM}', index as any);
    galleryImages.push(finalImageURL);
  }

  return galleryImages;
};


export function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    // @ts-ignore
    var context = this, args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

export const formatRangeLimit = (value: number[], maxValue: number): number[] => {
  if (value && value.length) {
    const maxRange = value[1];
    if (maxRange && maxRange === -1) {
      return [value[0] || 0, maxValue]
    }
  }
  return value;
}

export const getStepsByRange = (
  steps: number,
  step: number,
  minValue: number,
  maxValue: number,
  format: (number: number) => string,
  initialIndex: number
) => {
  const stepsMarks = [
    {
      value: minValue,
      label: format(minValue),
    },
  ];
  for (let index = initialIndex; index < steps; index++) {
    stepsMarks.push({
      value: index * step,
      label: "",
    });
  }

  stepsMarks.push({
    value: maxValue,
    label: format(maxValue),
  });
  return stepsMarks;
};

/**
 * Convert a number in price format
 * @param number
 * @returns {string} the price format
 */
export const formatNumberWithCommas = (
  number: number,
  currencySymbol: string,
): string =>
  currencySymbol
    ? `${currencySymbol}${number.toLocaleString()}`
    : number.toLocaleString();


/**
 * Get Vehicle Image URL using Vilma Service
 * @param faServicesURL
 * @param vin
 * @param serviceConfigsServiceConfig
 * @param imageParams
 * @returns Vehicle Image URL string
 */
export const getVehicleImage = (
  faServicesURL: string,
  vin?: string,
  serviceConfigsServiceConfig?: string,
  imageParams?: ImageParams,
) => {
  const serviceParams = new URLSearchParams();
  const defaultImageParams = new URLSearchParams({
    width: '480',
  } as any).toString() as ImageParams;

  const vehicleImageParams =
    imageParams && Object.keys(imageParams).length > 0
      ? (new URLSearchParams(imageParams as any).toString() as ImageParams)
      : defaultImageParams;

  if (serviceConfigsServiceConfig) {
    serviceParams.set(
      'serviceConfigsServiceConfig',
      serviceConfigsServiceConfig,
    );
  }

  return `${faServicesURL}/vehicleImage?vin=${vin}&${vehicleImageParams}&${serviceParams.toString()}`;
};

export const getOnlineBuyingProvider = (
  vehicle: UsedVehicle,
) => {
  // using provider set on config file
  let provider = '';

  // if there is data from graphql use that provider
  if (vehicle.dealerProvider) {
    provider = vehicle.dealerProvider
      .replace('.', '')
      .replace(/\s/g, '')
      .toLowerCase();
  }

  return provider;
};

type QueryParams = { [key: string]: string | number };

export const addQueryParams = (url: string, queryParams: QueryParams) => {
  const hashIndex = url.indexOf('#');
  let baseUrl = url;
  let hashPart = '';

  if (hashIndex !== -1) {
    baseUrl = url.slice(0, hashIndex);
    hashPart = url.slice(hashIndex);
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  const queryString = Object.keys(queryParams)
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');

  return `${baseUrl}${separator}${queryString}${hashPart}`;
};

interface IAdobe {
  marketingCloudVisitorID?: string;
}

declare var s: IAdobe;

export const getOnlineBuyingDeeplinkData = (vehicle: UsedVehicle) => {
  //If this method is called it means the validations that is enrolled has been made

  // Get local config about providers
  const { drproviders } = useFeatureAppConfig();

  const provider = getOnlineBuyingProvider(vehicle);

  const providerConfig = drproviders[provider ?? ''];

  const isDeepLink: boolean =  !!(vehicle.dealerDeeplink && providerConfig['turnOnDeeplinkRedirect'])
  let obLink =
    isDeepLink
      ? vehicle.dealerDeeplink
      : vehicle.onlineSalesURL;


  let isLinKModified = isOnlineBuyingLinkModified(vehicle);

  if (obLink && isLinKModified && isDeepLink) {
    obLink = addQueryParams(obLink, {
      sd_referrer: 'cpodigitalretailing.vw.com',
      ...(typeof s !== 'undefined' &&
        s?.marketingCloudVisitorID && {
          mid: s.marketingCloudVisitorID,
        }),
    });
  } else if (obLink) {
    obLink = addQueryParams(obLink, {
      sd_referrer: 'www.vw.com',
      ...(typeof s !== 'undefined' &&
        s?.marketingCloudVisitorID && {
          mid: s.marketingCloudVisitorID,
        }),
    });
  }

  return { obLink, isLinKModified };
};
